import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LayoutPrefsProvider } from "@/contexts/LayoutPrefsContext";
import { ProtectedRoute } from "@/components/app/ProtectedRoute";
import DashboardLayout from "@/layout/DashboardLayout";

import LoginPage from "@/pages/auth/LoginPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import AcceptInvitePage from "@/pages/auth/AcceptInvitePage";
import ProfilePage from "@/pages/auth/ProfilePage";
import SignupPage from "@/pages/auth/SignupPage";
import ReportsPage from "@/pages/reports/ReportsPage";

import DashboardHome from "@/pages/dashboard/DashboardHome";
import UsersPage from "@/pages/users/UsersPage";
import GroupsPage from "@/pages/groups/GroupsPage";
import RolesPage from "@/pages/roles/RolesPage";
import AuditPage from "@/pages/audit/AuditPage";
import SettingsPage from "@/pages/settings/SettingsPage";
import FormsListPage from "@/pages/forms/FormsListPage";
import FormBuilderPage from "@/pages/forms/FormBuilderPage";
import FormRecordsPage from "@/pages/forms/FormRecordsPage";
import RecordEditorPage from "@/pages/forms/RecordEditorPage";
import FormFillPage from "@/pages/forms/FormFillPage";
import WorkflowEditorPage from "@/pages/forms/WorkflowEditorPage";
import WorkflowHistoryPage from "@/pages/forms/WorkflowHistoryPage";
import WorkflowHistoryDetailPage from "@/pages/forms/WorkflowHistoryDetailPage";
import AppsPortal from "@/pages/portal/AppsPortal";
import ApprovalsPage from "@/pages/approvals/ApprovalsPage";
import BillingPage from "@/pages/billing/BillingPage";
import PlatformLayout from "@/layout/PlatformLayout";
import PlatformDashboard from "@/pages/platform/PlatformDashboard";
import PlatformOrganizationsPage from "@/pages/platform/PlatformOrganizationsPage";
import PlatformBillingPage from "@/pages/platform/PlatformBillingPage";
import PlatformPaymentsPage from "@/pages/platform/PlatformPaymentsPage";
import PlatformPricingPage from "@/pages/platform/PlatformPricingPage";
import PlatformDemoRequestsPage from "@/pages/platform/PlatformDemoRequestsPage";
import PlatformTemplatesPage from "@/pages/platform/PlatformTemplatesPage";
import PlatformEmailSettingsPage from "@/pages/platform/PlatformEmailSettingsPage";
import PlatformThemesPage from "@/pages/platform/PlatformThemesPage";
import LandingPage from "@/pages/landing/LandingPage";
import PublicOrgLanding from "@/pages/public/PublicOrgLanding";
import PublicFormFill from "@/pages/public/PublicFormFill";
import QuickApprovalPage from "@/pages/approvals/QuickApprovalPage";
import PrivacyPolicyPage from "@/pages/legal/PrivacyPolicyPage";
import HelpPage from "@/pages/legal/HelpPage";
import TermsAndConditionsPage from "@/pages/legal/TermsAndConditionsPage";
import RefundPolicyPage from "@/pages/legal/RefundPolicyPage";

const ADMIN = ["platform_admin", "org_admin"];
const PLATFORM_ONLY = ["platform_admin"];
const TOAST_OPTIONS = { className: "!rounded-sm !font-mono !text-xs" };

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <ThemeProvider>
                        <LayoutPrefsProvider>
                        <Toaster position="top-right" toastOptions={TOAST_OPTIONS} />
                    <Routes>
                        {/* Public landing */}
                        <Route path="/" element={<LandingPage />} />

                        {/* Legal */}
                        <Route path="/privacy" element={<PrivacyPolicyPage />} />
                        <Route path="/help" element={<HelpPage />} />
                        <Route path="/terms" element={<TermsAndConditionsPage />} />
                        <Route path="/refund" element={<RefundPolicyPage />} />

                        {/* Magic-link approvals (no auth) */}
                        <Route path="/quick-approve" element={<QuickApprovalPage defaultAction="approve" />} />
                        <Route path="/quick-reject" element={<QuickApprovalPage defaultAction="reject" />} />

                        {/* Public org-scoped pages (anonymous) */}
                        <Route path="/p/:slug" element={<PublicOrgLanding />} />
                        <Route path="/p/:slug/:formCode" element={<PublicFormFill />} />

                        {/* Public auth (no self-signup) */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/signup" element={<SignupPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/accept-invite" element={<AcceptInvitePage />} />
                        <Route path="/register" element={<Navigate to="/signup" replace />} />

                        {/* Protected: form fill (no sidebar - clean fill experience) */}
                        <Route path="/forms/:id/fill" element={<ProtectedRoute><FormFillPage /></ProtectedRoute>} />

                        {/* Platform admin portal */}
                        <Route element={<ProtectedRoute requireRole={PLATFORM_ONLY}><PlatformLayout /></ProtectedRoute>}>
                            <Route path="/platform" element={<PlatformDashboard />} />
                            <Route path="/platform/organizations" element={<PlatformOrganizationsPage />} />
                            <Route path="/platform/billing" element={<PlatformBillingPage />} />
                            <Route path="/platform/payments" element={<PlatformPaymentsPage />} />
                            <Route path="/platform/pricing" element={<PlatformPricingPage />} />
                            <Route path="/platform/demo-requests" element={<PlatformDemoRequestsPage />} />
                            <Route path="/platform/templates" element={<PlatformTemplatesPage />} />
                            <Route path="/platform/email-settings" element={<PlatformEmailSettingsPage />} />
                            <Route path="/platform/themes" element={<PlatformThemesPage />} />
                        </Route>

                        {/* Protected app shell */}
                        <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                            <Route path="/portal" element={<AppsPortal />} />
                            <Route path="/approvals" element={<ApprovalsPage />} />
                            <Route path="/workflow-history" element={<ProtectedRoute requireRole={ADMIN}><WorkflowHistoryPage /></ProtectedRoute>} />
                            <Route path="/workflow-history/:recordId" element={<ProtectedRoute requireRole={ADMIN}><WorkflowHistoryDetailPage /></ProtectedRoute>} />
                            <Route path="/dashboard" element={<DashboardHome />} />
                            <Route path="/forms" element={<FormsListPage />} />
                            <Route path="/forms/:id/builder" element={<ProtectedRoute requireRole={ADMIN}><FormBuilderPage /></ProtectedRoute>} />
                            <Route path="/forms/:id/workflow" element={<ProtectedRoute requireRole={ADMIN}><WorkflowEditorPage /></ProtectedRoute>} />
                            <Route path="/forms/:id/records" element={<FormRecordsPage />} />
                            <Route path="/forms/:id/records/new" element={<RecordEditorPage />} />
                            <Route path="/forms/:id/records/:recordId/edit" element={<RecordEditorPage />} />
                            <Route path="/profile" element={<ProfilePage />} />
                            <Route path="/users" element={<ProtectedRoute requireRole={ADMIN}><UsersPage /></ProtectedRoute>} />
                            <Route path="/groups" element={<ProtectedRoute requireRole={ADMIN}><GroupsPage /></ProtectedRoute>} />
                            <Route path="/roles" element={<ProtectedRoute requireRole={ADMIN}><RolesPage /></ProtectedRoute>} />
                            <Route path="/billing" element={<ProtectedRoute requireRole={ADMIN}><BillingPage /></ProtectedRoute>} />
                            <Route path="/reports" element={<ReportsPage />} />
                            <Route path="/audit" element={<ProtectedRoute requireRole={ADMIN}><AuditPage /></ProtectedRoute>} />
                            <Route path="/settings" element={<ProtectedRoute requireRole={ADMIN}><SettingsPage /></ProtectedRoute>} />
                        </Route>

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                        </LayoutPrefsProvider>
                    </ThemeProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
